/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router';
import { InfoCircle } from '@saleshandy/icons';
import { ImageIcon } from '@saleshandy/design-system';
import type { IProps } from './lead-finder-plans-container';
import BillingSummaryModal from './components/billing-summary-modal';
import PaddleBillingSummaryModal from '../outreach-plans/components/paddle-billing-summary-modal/paddle-billing-summary-modal';
import { previewPaddleSubscription, getUpdatePaymentMethodTransaction } from '../../helpers/billing-subscription.api';
import usePaddleCustomerAuthToken from '../../../../../../shared/hooks/use-paddle-customer-auth-token';
import type { PaddlePreviewSubscriptionResponse } from '../../types/paddle';
import toaster from '../../../../../../shared/toaster';
import Button from '../../../../../../shared/design-system/components/atoms/button';
import { executeOnRequestStatus } from '../../../../../../shared/utils/execute-on-request-status';
import { showGeneralErrorNotification } from '../../../../../../shared/utils/errors';
import {
  getIsLeadFinderUserSubscribed,
  isRequestPending,
} from '../../utils/helper';
import PlansSection from './components/plans-section';
import { BillingOption } from '../../../../../../shared/utils/billing-options';
import { getDisabledLabel } from '../modify-subscription/utils/helper';
import { Images } from '../../../../../../shared/app-constants';
import {
  LeadFinderEmailOnlySubscriptionPlans,
  SubscriptionPlanTitle,
} from '../../../../../../shared/utils/subscription-plans';
import { redirectWithoutRefresh } from '../../../../../../shared/utils';
import { showToastMessage } from '../../../../../../shared/design-system/components/molecules/upgrade-plan-billing-section/helper/helper';
import {
  getPlanPeriod,
  getSubscribeAction,
  UpgradePlanQueryParamsKey,
  isPremiumFeatureBannerShow,
} from './utils/helper';
import { openChatSupport } from '../../../../../../shared/utils/open-chat-support';
import { OverlayTooltip } from '../../../../../../shared/design-system/components/overlay';
import { Placement } from '../../../../../../shared/design-system/components/overlay/enums';
import { Routes } from '../../../../../../shared/routes';
import PlanCycleSwitch from '../../../../../../shared/design-system/components/molecules/plan-cycle-switch';
import {
  getLeadFinderPlanMetaData,
  getLeadFinderCredits,
} from './components/lead-finder-plan-card/utils/helper';
import CreditSlider from './components/credit-slider/credit-slider';
import {
  LeadFinderAddonPlan,
  LeadFinderBasePlan,
  LeadFinderComboPlan,
} from '../../types/response/get-lead-finder-plans';
import UpgradePlanStatic from '../upgrade-plan-static/upgrade-plan-static';
import { supportUrls } from '../../../../../../shared/utils/urls';
import usePaddleIntegration from '../../../../../../shared/hooks/script-integrations/usePaddleIntegration';
import usePaddleCoupon from '../../hooks/use-paddle-coupon';

const MANUALLY_MODIFY_SUBSCRIPTION_MSG =
  'Your subscription was created manually. Please contact the support team to modify your subscription.';

const extractTier = (planCode: string): string => {
  if (planCode.includes('starter')) return 'starter';
  if (planCode.includes('scale')) return 'scale';
  return 'pro';
};

const LeadFinderPlans: React.FC<IProps> = ({
  //* API Request State
  getCouponCodeResponse,
  sendGetLeadFinderSubscriptionRequestStatus,
  sendGetLeadFinderSubscriptionResponse,
  sendGetLeadFinderPlansResponse,
  sendPurchaseLeadFinderSubscriptionRequestStatus,
  sendPurchaseLeadFinderSubscriptionRequestMessage,
  sendPurchaseLeadFinderModifySubscriptionRequestStatus,
  sendPurchaseLeadFinderModifySubscriptionRequestMessage,
  leadFinderSubscriptionPlanResponse,
  //* API Request
  sendPurchaseLeadFinderSubscriptionRequest,
  sendPurchaseLeadFinderModifySubscriptionRequest,
  sendGetCalculatePayRequest,
  sendGetLeadFinderSubscriptionRequest,
  sendGetUserSettingsRequest,
  sendGetUserSubscriptionMetadataRequest,
  resetSubscription,
  resetCalculatePayState,
  paymentProvider,
  userId,
  shAccountId,
  userEmail,
  paddlePolling,
  startPaddlePollingThunk,
  resetPurchaseLeadFinderSubscriptionRequestStatus,
  resetPurchaseLeadFinderModifySubscriptionResponse,
  paddleCustomerId,
  sendGetPostLoadMetaRequest,
}) => {
  const location = useLocation();
  const history = useHistory();

  //* States
  const getInitialPlanCycle = (): BillingOption => {
    const currentPlanCode = sendGetLeadFinderSubscriptionResponse?.planCode;
    const currentPlanMode = sendGetLeadFinderSubscriptionResponse?.planMode;
    const isNewPlanUser = currentPlanCode?.startsWith('lf');
    if (isNewPlanUser && currentPlanMode === 1) {
      return BillingOption.MONTHLY;
    }
    return BillingOption.ANNUALLY;
  };
  const [planCycle, setPlanCycle] = useState<BillingOption>(
    getInitialPlanCycle(),
  );
  const [
    isBillingSummaryModalShow,
    setIsBillingSummaryModalShow,
  ] = useState<boolean>(false);
  const [isUserModifyingSubscription, setIsModifySubscription] = useState(
    false,
  );
  const [selectedPlanDetails, setSelectedPlanDetails] = useState<
    LeadFinderBasePlan | LeadFinderComboPlan
  >(null);
  const [isEmailPhonePlan, setIsEmailPhonePlan] = useState<boolean>(false);

  const [creditSliderSelectedValue, setCreditSliderSelectedValue] = useState<
    number[]
  >([0]);
  const [
    selectedLFCreditPlan,
    setSelectedLFCreditPlan,
  ] = useState<LeadFinderAddonPlan>(null);
  const [selectedBasePlanId, setSelectedBasePlanId] = useState<number>(null);
  const [paddleBillingData, setPaddleBillingData] = useState<{
    basePlanAmount: number;
    addonAmount?: number;
    addonCredits?: number;
    addonName?: string;
    planAmount: number;
    nextBillingAmount: number;
    billingCycle: number;
    planName: string;
  } | null>(null);

  // Derive addon plans filtered by current plan cycle from combo plans
  const planMode = planCycle === BillingOption.ANNUALLY ? 12 : 1;
  const filteredAddonPlans = useMemo(() => {
    const comboPlans = sendGetLeadFinderPlansResponse?.comboPlans || [];
    const addonMap = new Map<number, LeadFinderAddonPlan>();
    comboPlans.forEach((combo) => {
      if (combo.planMode === planMode && !addonMap.has(combo.addonPlan.id)) {
        addonMap.set(combo.addonPlan.id, combo.addonPlan);
      }
    });
    return Array.from(addonMap.values());
  }, [sendGetLeadFinderPlansResponse, planMode]);

  // Build combo plan lookup map for O(1) lookups
  const comboPlanLookup = useMemo(() => {
    const lookup = new Map<string, LeadFinderComboPlan>();
    sendGetLeadFinderPlansResponse?.comboPlans?.forEach((combo) => {
      const key = `${combo.planMode}-${combo.addonPlan.credits}-${extractTier(combo.planCode)}`;
      lookup.set(key, combo);
    });
    return lookup;
  }, [sendGetLeadFinderPlansResponse]);

  // Disable upgrade when selected total amount is less than current subscription amount (new plans only)
  const isUpgradeDisabled = useMemo(() => {
    const currentPlanCode = sendGetLeadFinderSubscriptionResponse?.planCode;
    const isNewPlanUser = currentPlanCode?.startsWith('lf');

    // Old plan user or free/trial — always allow upgrade
    if (!isNewPlanUser) return false;

    const currentPlanAmount = parseFloat(
      sendGetLeadFinderSubscriptionResponse?.planAmount || '0',
    );
    const currentAddonAmount = parseFloat(
      sendGetLeadFinderSubscriptionResponse?.addOnPlan?.amount || '0',
    );
    const currentTotalAmount = currentPlanAmount + currentAddonAmount;

    const selectedBasePlan = sendGetLeadFinderPlansResponse?.basePlans?.find(
      ({ id }) => id === selectedBasePlanId,
    );
    const selectedBaseAmount = parseFloat(selectedBasePlan?.amount || '0');
    const selectedAddonAmount = selectedLFCreditPlan?.amount || 0;
    const selectedTotalAmount = selectedBaseAmount + selectedAddonAmount;

    return selectedTotalAmount <= currentTotalAmount;
  }, [
    selectedBasePlanId,
    selectedLFCreditPlan,
    sendGetLeadFinderSubscriptionResponse,
    sendGetLeadFinderPlansResponse,
  ]);

  // Detect addon-only change: same base tier & billing cycle, just adding/changing addon
  const isAddonOnlyChange = useMemo(() => {
    const currentPlanCode = sendGetLeadFinderSubscriptionResponse?.planCode;
    if (!currentPlanCode?.startsWith('lf')) return false;
    if (!isUserModifyingSubscription) return false;
    if (!selectedLFCreditPlan) return false;

    const basePlan = sendGetLeadFinderPlansResponse?.basePlans?.find(
      ({ id }) => id === selectedBasePlanId,
    );
    if (!basePlan) return false;

    const currentTier = extractTier(currentPlanCode);
    const selectedTier = extractTier(basePlan.planCode);
    const currentPlanMode = sendGetLeadFinderSubscriptionResponse?.planMode;

    return (
      currentTier === selectedTier && currentPlanMode === basePlan.planMode
    );
  }, [
    selectedBasePlanId,
    selectedLFCreditPlan,
    isUserModifyingSubscription,
    sendGetLeadFinderSubscriptionResponse,
    sendGetLeadFinderPlansResponse,
  ]);

  const [
    isPaddleBillingSummaryModalShow,
    setIsPaddleBillingSummaryModalShow,
  ] = useState(false);
  const [
    paddlePreviewData,
    setPaddlePreviewData,
  ] = useState<PaddlePreviewSubscriptionResponse | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPaymentMethodUpdate, setIsPaymentMethodUpdate] = useState(false);
  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);

  const { getToken: getCustomerAuthToken } = usePaddleCustomerAuthToken(paddleCustomerId);

  const {
    isCouponApplying,
    isCouponApplied,
    isCouponInvalid,
    preAppliedCouponCode,
    onApplyCoupon: onPaddleApplyCoupon,
    onClearCoupon: onPaddleClearCoupon,
    resetCouponState,
  } = usePaddleCoupon({
    planId: selectedPlanDetails?.id,
    paddlePriceId: selectedPlanDetails?.paddlePlanId,
    application: 'lead-finder',
    shAccountId,
    onPreviewUpdate: setPaddlePreviewData,
  });

  const LEAD_FINDER_SUBSCRIPTION_URL = '/billing/subscriptions/lead-finder';

  const isPaddlePolling = paddlePolling?.isActive && paddlePolling?.applicationType === 'lead-finder';

  const isFreePlanPaddleUser =
    !isUserModifyingSubscription && paymentProvider === 'paddle';

  const handleCheckoutStateReset = useCallback(() => {
    setIsBillingSummaryModalShow(false);
    setIsPaddleBillingSummaryModalShow(false);
    setPaddlePreviewData(null);
    setPaddleBillingData(null);
    resetCouponState();
  }, [resetCouponState]);

  const { isReady: isPaddleReady, openCheckout } = usePaddleIntegration({
    onCheckoutComplete: (data) => {
      if (isPaymentMethodUpdate) {
        const payment = data?.data?.payment;
        const card = payment?.method_details?.card;
        if (card && paddlePreviewData) {
          setPaddlePreviewData({
            ...paddlePreviewData,
            payload: {
              ...paddlePreviewData.payload,
              cardDetails: [{
                paymentMethodId: payment.payment_method_id || '',
                type: payment.method_details?.type || 'card',
                card: {
                  type: card.type || '',
                  last4: card.last4 || '',
                  expiryMonth: card.expiry_month,
                  expiryYear: card.expiry_year,
                  cardholderName: card.cardholder_name || '',
                },
              }],
            },
          });
        }
        if (window.Paddle?.Checkout?.close) {
          window.Paddle.Checkout.close();
        }
        return;
      }
      if (window.Paddle?.Checkout?.close) {
        window.Paddle.Checkout.close();
      }
      handleCheckoutStateReset();
      redirectWithoutRefresh(LEAD_FINDER_SUBSCRIPTION_URL);
      startPaddlePollingThunk({
        shAccountId,
        applicationType: 'lead-finder',
        planId: selectedPlanDetails?.id,
      });
    },
    onCheckoutError: () => {
      handleCheckoutStateReset();
    },
    onCheckoutPaymentError: () => {
      handleCheckoutStateReset();
      toaster.error('Payment failed. Please try again.');
      redirectWithoutRefresh(LEAD_FINDER_SUBSCRIPTION_URL);
    },
    onError: () => {
      toaster.error('Something went wrong. Please try again.');
    },
    onCheckoutClosed: () => {
      if (isPaymentMethodUpdate) {
        setIsPaymentMethodUpdate(false);
        return;
      }
      handleCheckoutStateReset();
    },
  });

  const showBillingSummaryModal = () => setIsBillingSummaryModalShow(true);
  const hideBillingSummaryModal = () => {
    setIsBillingSummaryModalShow(false);
    setPaddleBillingData(null);
    resetQueryParams();
    resetCouponState();
  };

  const handelBillingOption = (value: BillingOption) => setPlanCycle(value);

  const onSelectPlan = (planId: number, planName: string) => {
    if (planName === SubscriptionPlanTitle.Custom) {
      openChatSupport();
      return;
    }

    setSelectedBasePlanId(planId);
  };

  const handleUpgradeClick = async () => {
    if (!selectedBasePlanId) return;
    if (isPaddlePolling) return;

    let selectedPlan: LeadFinderBasePlan | LeadFinderComboPlan;
    const basePlan = sendGetLeadFinderPlansResponse.basePlans.find(
      ({ id }) => id === selectedBasePlanId,
    );

    if (!selectedLFCreditPlan) {
      selectedPlan = basePlan;
    } else if (basePlan) {
      const baseTier = extractTier(basePlan.planCode);
      const key = `${basePlan.planMode}-${selectedLFCreditPlan.credits}-${baseTier}`;
      selectedPlan = comboPlanLookup.get(key);
    }

    if (!selectedPlan) return;


    setSelectedPlanDetails(selectedPlan);
    resetCalculatePayState();

    if (isFreePlanPaddleUser) {
      if (!basePlan?.paddlePlanId || !isPaddleReady) {
        return;
      }

      const isYearly = basePlan.planMode === 12;
      const basePlanAmount = parseFloat(basePlan.amount);
      const addonAmount = selectedLFCreditPlan?.amount;
      const totalAmount = basePlanAmount + (addonAmount || 0);

      setPaddleBillingData({
        basePlanAmount,
        ...(selectedLFCreditPlan && {
          addonAmount,
          addonCredits: selectedLFCreditPlan.credits,
          addonName: selectedLFCreditPlan.name,
        }),
        planAmount: totalAmount,
        nextBillingAmount: totalAmount,
        billingCycle: isYearly ? 365 : 30,
        planName: basePlan.planName,
      });

      showBillingSummaryModal();
      updateQueryParams(selectedPlan.id);
      return;
    }

    // Existing subscriber + Paddle: show PaddleBillingSummaryModal with preview
    if (paymentProvider === 'paddle') {
      setIsPreviewLoading(true);
      setIsPaddleBillingSummaryModalShow(true);
      try {
        const updateType = getUpdateType();
        const response = await previewPaddleSubscription({
          accountSubscriptionPlanId: selectedPlan.id,
          shAccountId,
          application: 'lead-finder',
          updateType,
        });
        setPaddlePreviewData(response);
      } catch {
        toaster.error('Failed to load billing preview. Please try again.');
        setIsPaddleBillingSummaryModalShow(false);
      } finally {
        setIsPreviewLoading(false);
      }
      updateQueryParams(selectedPlan.id);
      return;
    }

    // Stripe: existing flow
    sendGetCalculatePayRequest({
      accountSubscriptionPlanId: selectedPlan.id,
      slots: 1,
    });

    showBillingSummaryModal();
    updateQueryParams(selectedPlan.id);
  };

  const onPaddleFreePayNow = async () => {
    if (!selectedBasePlanId) return;

    const basePlan = sendGetLeadFinderPlansResponse?.basePlans?.find(
      ({ id }) => id === selectedBasePlanId,
    );
    const mainPaddlePlanId = basePlan?.paddlePlanId;
    if (!mainPaddlePlanId || !isPaddleReady) return;

    const items: { priceId: string; quantity: number }[] = [
      { priceId: mainPaddlePlanId, quantity: 1 },
    ];

    if (selectedLFCreditPlan?.paddleId) {
      items.push({ priceId: selectedLFCreditPlan.paddleId, quantity: 1 });
    }

    const authToken = await getCustomerAuthToken();
    openCheckout({
      items,
      ...(authToken && { customerAuthToken: authToken }),
      customer: {
        ...(paddleCustomerId
          ? { id: paddleCustomerId }
          : { email: userEmail }),
      },
      customData: {
        user_id: userId,
        sh_account_id: shAccountId,
        is_addon_upgrade: true,
        update_type: 'addon_upgrade',
      },
      settings: {
        displayMode: 'overlay',
        variant: 'one-page',
        theme: 'light',
        ...(paddleCustomerId && { allowLogout: false }),
      },
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onPurchase = (token: any, couponCode: string) => {
    if (!isUserModifyingSubscription) {
      const payload = {
        ...(token && { slots: 1 }),
        planId: Number(selectedPlanDetails?.id),
        ...(token && { stripeCardToken: token.id }),
        ...(getCouponCodeResponse &&
          !getCouponCodeResponse.isError &&
          couponCode && { couponCode }),
      };
      sendPurchaseLeadFinderSubscriptionRequest(payload);
    } else {
      const { customerId, subscriptionId } = sendGetLeadFinderSubscriptionResponse;
      if (!customerId || !subscriptionId) {
        showGeneralErrorNotification(MANUALLY_MODIFY_SUBSCRIPTION_MSG);
        return;
      }
      const payload = {
        accountSubscriptionPlanId: Number(selectedPlanDetails?.id),
        slots: 1,
        stripeCardToken: token.id,
        ...(getCouponCodeResponse &&
          !getCouponCodeResponse.isError &&
          couponCode && { couponCode }),
      };
      sendPurchaseLeadFinderModifySubscriptionRequest(payload);
    }
  };

  const getUpdateType = (): 'base_with_addon' | 'addon_only' | 'base_only' => {
    if (isAddonOnlyChange) return 'addon_only';
    if (selectedLFCreditPlan) return 'base_with_addon';
    return 'base_only';
  };

  const onPaddlePayNow = () => {
    if (!paddleCustomerId) {
      toaster.error('Unable to modify subscription. Please contact support.');
      return;
    }
    sendPurchaseLeadFinderModifySubscriptionRequest({
      accountSubscriptionPlanId: Number(selectedPlanDetails?.id),
      ...(preAppliedCouponCode && { couponCode: preAppliedCouponCode }),
      updateType: getUpdateType(),
    });
  };

  const onUpdateCardDetails = async () => {
    const {
      customerId,
      subscriptionId,
    } = sendGetLeadFinderSubscriptionResponse;
    if (!customerId || !subscriptionId) {
      showGeneralErrorNotification(MANUALLY_MODIFY_SUBSCRIPTION_MSG);
      return;
    }
    const authToken = await getCustomerAuthToken();
    try {
      setIsUpdatingPaymentMethod(true);
      const response = await getUpdatePaymentMethodTransaction(subscriptionId);
      const transactionId = (response as any)?.payload?.transactionId;
      if (!transactionId) return;

      setIsPaymentMethodUpdate(true);
      openCheckout({
        transactionId,
        ...(authToken && { customerAuthToken: authToken }),
        settings: { displayMode: 'overlay', theme: 'light', variant: 'one-page' },
      });
    } catch {
      toaster.error('Failed to update payment method. Please try again.');
    } finally {
      setIsUpdatingPaymentMethod(false);
    }
  };

  const hidePaddleBillingSummaryModal = () => {
    setIsPaddleBillingSummaryModalShow(false);
    setPaddlePreviewData(null);
    resetQueryParams();
    resetCouponState();
  };

  const sendPurchaseSubscriptionRequestSuccess = (message: string) => {
    showToastMessage(message);

    sendGetLeadFinderSubscriptionRequest();
    redirectWithoutRefresh(
      '/billing/subscriptions/lead-finder?plan=subscribed',
    );

    resetSubscription();

    setTimeout(() => {
      // Stripe is taking time to update payment is pending or not data
      sendGetUserSettingsRequest();
      sendGetUserSubscriptionMetadataRequest();
      sendGetPostLoadMetaRequest();
    }, 500);
  };

  const updateQueryParams = (planId: number) => {
    const queryParams = new URLSearchParams(location.search);
    const allPlans = [
      ...sendGetLeadFinderPlansResponse.basePlans,
      ...sendGetLeadFinderPlansResponse.comboPlans,
    ];
    const index = allPlans.findIndex(({ id }) => id === planId);
    const selectPlanIndex = index + 1;

    queryParams.set(
      UpgradePlanQueryParamsKey.Action,
      getSubscribeAction(isUserModifyingSubscription),
    );
    queryParams.set(UpgradePlanQueryParamsKey.Period, getPlanPeriod(planCycle));
    queryParams.set(UpgradePlanQueryParamsKey.Plan, selectPlanIndex.toString());

    history.push({
      pathname: Routes.Setting_Billing_Upgrade_Lead_Finder_Plan,
      search: queryParams.toString(),
    });
  };

  const resetQueryParams = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete(UpgradePlanQueryParamsKey.Action);
    queryParams.delete(UpgradePlanQueryParamsKey.Period);
    queryParams.delete(UpgradePlanQueryParamsKey.Plan);

    history.push({
      pathname: Routes.Setting_Billing_Upgrade_Lead_Finder_Plan,
      search: queryParams.toString(),
    });
  };

  const getPremiumFeatureTooltipText = (): string =>
    'A/Z variant \nPipedrive Integration \nTeam Management \nAPI \nSender Rotation \nUnified Inbox Reply';

  const getActivePlanName = () => {
    const { planName } = sendGetLeadFinderSubscriptionResponse;
    const leadFinderPlanMetaData = getLeadFinderPlanMetaData(planName);

    return leadFinderPlanMetaData?.displayName || planName;
  };

  const handleCreditSliderChange = (value: number[]) => {
    const sliderPosition = value?.[0] ?? 0;
    setCreditSliderSelectedValue(value);

    if (sliderPosition === 0) {
      setSelectedLFCreditPlan(null);
    } else {
      const plan = filteredAddonPlans[sliderPosition - 1] ?? null;
      setSelectedLFCreditPlan(plan);
    }
  };

  //* useEffect
  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetLeadFinderSubscriptionRequestStatus,
      onSuccess: () => {
        const { planCode } = sendGetLeadFinderSubscriptionResponse;

        const isUserSubscribed = getIsLeadFinderUserSubscribed(planCode);
        setIsModifySubscription(isUserSubscribed);
      },
    });
  }, [sendGetLeadFinderSubscriptionRequestStatus]);

  // Purchase subscription
  useEffect(() => {
    executeOnRequestStatus({
      status: sendPurchaseLeadFinderSubscriptionRequestStatus,
      onSuccess: () => {
        sendPurchaseSubscriptionRequestSuccess(
          sendPurchaseLeadFinderSubscriptionRequestMessage,
        );
        resetPurchaseLeadFinderSubscriptionRequestStatus();
      },
    });
  }, [sendPurchaseLeadFinderSubscriptionRequestStatus]);

  // Modify subscription
  useEffect(() => {
    executeOnRequestStatus({
      status: sendPurchaseLeadFinderModifySubscriptionRequestStatus,
      onSuccess: () => {
        if (paymentProvider === 'paddle') {
          hidePaddleBillingSummaryModal();
          redirectWithoutRefresh(LEAD_FINDER_SUBSCRIPTION_URL);
          startPaddlePollingThunk({
            shAccountId,
            applicationType: 'lead-finder',
            planId: selectedPlanDetails?.id,
          });
        } else {
          sendPurchaseSubscriptionRequestSuccess(
            sendPurchaseLeadFinderModifySubscriptionRequestMessage,
          );
        }
        resetPurchaseLeadFinderModifySubscriptionResponse();
      },
    });
  }, [sendPurchaseLeadFinderModifySubscriptionRequestStatus]);

  useEffect(() => {
    if (leadFinderSubscriptionPlanResponse?.planCode) {
      if (
        LeadFinderEmailOnlySubscriptionPlans.includes(
          leadFinderSubscriptionPlanResponse?.planCode,
        )
      ) {
        setIsEmailPhonePlan(false);
      }
    }
  }, [
    leadFinderSubscriptionPlanResponse &&
    leadFinderSubscriptionPlanResponse?.planCode,
  ]);

  // Default select the current plan if it matches, otherwise Pro plan
  useEffect(() => {
    // Pre-select addon slider to current plan's addon credits if cycle matches.
    // For combo plans (e.g. "Pro + 500 Monthly"), addOnPlan may be null on the
    // subscription response, so first try matching the plan code in comboPlans.
    const currentPlanCode = sendGetLeadFinderSubscriptionResponse?.planCode;
    const currentSubscriptionPlanMode =
      sendGetLeadFinderSubscriptionResponse?.planMode;
    const currentCombo = sendGetLeadFinderPlansResponse?.comboPlans?.find(
      (c) => c.planCode === currentPlanCode,
    );
    const currentAddonCredits =
      currentCombo?.addonPlan?.credits ??
      sendGetLeadFinderSubscriptionResponse?.addOnPlan?.credits;

    if (currentAddonCredits && currentSubscriptionPlanMode === planMode) {
      const addonIndex = filteredAddonPlans.findIndex(
        (p) => Number(p.credits) === Number(currentAddonCredits),
      );
      if (addonIndex !== -1) {
        setCreditSliderSelectedValue([addonIndex + 1]);
        setSelectedLFCreditPlan(filteredAddonPlans[addonIndex]);
      } else {
        setCreditSliderSelectedValue([0]);
        setSelectedLFCreditPlan(null);
      }
    } else {
      setCreditSliderSelectedValue([0]);
      setSelectedLFCreditPlan(null);
    }

    const basePlans = sendGetLeadFinderPlansResponse?.basePlans;
    if (basePlans?.length) {
      const currentPlanCode = sendGetLeadFinderSubscriptionResponse?.planCode;
      const isNewPlanUser = currentPlanCode?.startsWith('lf');

      let matchedPlan: LeadFinderBasePlan | undefined;

      if (isNewPlanUser) {
        // New plan user: match by tier (starter/pro) and current cycle
        const isStarter = currentPlanCode.includes('starter');
        const tier = isStarter ? 'starter' : 'pro';
        matchedPlan = basePlans.find(
          (p) => p.planCode.includes(tier) && p.planMode === planMode,
        );
      }

      if (matchedPlan) {
        setSelectedBasePlanId(matchedPlan.id);
      } else {
        // Old plan user or no match: default to Pro
        const proPlan = basePlans.find(
          (p) => p.planCode.includes('pro') && p.planMode === planMode,
        );
        setSelectedBasePlanId(proPlan?.id || basePlans[0]?.id || null);
      }
    } else {
      setSelectedBasePlanId(null);
    }
  }, [
    planCycle,
    sendGetLeadFinderPlansResponse,
    sendGetLeadFinderSubscriptionResponse,
  ]);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      setPlanCycle(BillingOption.ANNUALLY);
      setIsBillingSummaryModalShow(false);
      setIsModifySubscription(false);
      setIsEmailPhonePlan(false);
      setSelectedPlanDetails(null);
      setSelectedBasePlanId(null);
      setPaddleBillingData(null);
    };
  }, []);
  const isPurchaseInProgress =
    isRequestPending(sendPurchaseLeadFinderSubscriptionRequestStatus) ||
    isRequestPending(sendPurchaseLeadFinderModifySubscriptionRequestStatus);

  const getLeadFinderBillingDetails = () => {
    const basePlan = sendGetLeadFinderPlansResponse?.basePlans?.find(
      ({ id }) => id === selectedBasePlanId,
    );
    if (!basePlan) return undefined;

    const meta = getLeadFinderPlanMetaData(basePlan.planName);
    return {
      updateType: getUpdateType(),
      displayPlanName: meta?.displayName || 'Plan',
      billingPeriod: (basePlan.planMode === 1 ? 'month' : 'year') as
        | 'month'
        | 'year',
      basePlanAmount: parseFloat(basePlan.amount),
      basePaddlePriceId: basePlan.paddlePlanId,
      ...(selectedLFCreditPlan && {
        addonCredits: selectedLFCreditPlan.credits,
        addonAmount: selectedLFCreditPlan.amount,
      }),
    };
  };

  return (
    <div className="outreach-plans-lead-finder">
      {/* Premium Feature Info Banner */}
      {isPremiumFeatureBannerShow(
        sendGetLeadFinderSubscriptionResponse?.planCode,
      ) && (
          <div className="d-flex justify-content-center">
            <div className="pt-3 pb-3 pr-4 pl-4 mt-4 gray-1 text-center premium-feature-card">
              <span className="regular-1 popover-arrow-color-txt">
                You have used any of these{' '}
              </span>
              <OverlayTooltip
                text={getPremiumFeatureTooltipText()}
                className="line-break"
              >
                <span className="semibold-1 blue-txt-11 pointer">
                  premium feature
                </span>
              </OverlayTooltip>
              <span className="regular-1 popover-arrow-color-txt">
                <ImageIcon src={Images.PremiumFeature} alt="premium feature" /> in
                the trial. We suggest upgrading to Outreach PRO or a higher plan.
                Otherwise, you will lose access to all the premium features.
              </span>
            </div>
          </div>
        )}
      <div className="lead-finder-plans-header">
        <h1>Find the right Lead Finder plan</h1>
        <span>
          You are currently on the{' '}
          <span className="semibold-1">{getActivePlanName()} plan</span>
        </span>
      </div>


      <div className="d-flex flex-column justify-content-center align-items-center">
        <div className="d-flex flex-column align-items-end mt-4">
          <PlanCycleSwitch
            value={planCycle}
            onChange={handelBillingOption}
            disabledLabel={getDisabledLabel(
              sendGetLeadFinderSubscriptionResponse,
            )}
          />
        </div>
      </div>
      <div className="lead-finder-plans-section">
        <PlansSection
          onSelectPlan={onSelectPlan}
          planCycle={planCycle}
          selectedPlanId={selectedPlanDetails?.id}
          selectedBasePlanId={selectedBasePlanId}
          isEmailPhonePlan={isEmailPhonePlan}
          isPaddlePolling={isPaddlePolling}
        />
        <div className="credit-slider-container">
          <div className="credit-slider-container-header">
            <div className="credit-slider-container-header-title-wrapper">
              <span className="credit-slider-container-header-title">
                Add on Credits
                <OverlayTooltip
                  text="Add-on credits are billed with your plan and renew each billing cycle."
                  className="tooltip__max-width"
                  placement={Placement.Top}
                >
                  <InfoCircle className="ml-2" style={{ cursor: 'help' }} />
                </OverlayTooltip>
              </span>
              <span className="credit-slider-container-header-description">
                Add more credits to your plan to enrich more leads—reveal
                emails, phone numbers, and company details.
              </span>
            </div>
          </div>
          <div className="credit-slider-wrapper">
            <div className="credit-slider-selected-value-wrapper">
              <div className="credit-slider-selected-value">
                {(() => {
                  const credits = getLeadFinderCredits();
                  const isYearly = planCycle === BillingOption.ANNUALLY;
                  const periodSuffix = isYearly ? '/year' : '/month';
                  const basePlan = selectedBasePlanId
                    ? sendGetLeadFinderPlansResponse?.basePlans?.find(
                      ({ id }) => id === selectedBasePlanId,
                    )
                    : null;

                  if (!basePlan) {
                    return selectedLFCreditPlan
                      ? `${selectedLFCreditPlan.name} credits${periodSuffix}`
                      : '0 Credits';
                  }

                  const isStarter = basePlan.planCode.includes('starter');
                  const planCredits = isYearly
                    ? isStarter
                      ? credits.yearly.starter
                      : credits.yearly.pro
                    : isStarter
                      ? credits.monthly.starter
                      : credits.monthly.pro;
                  const planCreditsFormatted =
                    planCredits >= 1000
                      ? `${planCredits / 1000}K`
                      : planCredits.toString();

                  return (
                    <>
                      <span className="font-weight-bold">
                        {planCreditsFormatted}
                      </span>
                      <span className="credit-slider-selected-plan-label">
                        {' '}
                        (
                        {(() => {
                          const currentPlanCode =
                            sendGetLeadFinderSubscriptionResponse?.planCode;
                          const currentPlanMode =
                            sendGetLeadFinderSubscriptionResponse?.planMode;
                          const isNewPlanUser = currentPlanCode?.startsWith(
                            'lf',
                          );
                          if (isNewPlanUser) {
                            const currentTier = extractTier(currentPlanCode);
                            const selectedTier = extractTier(
                              basePlan.planCode,
                            );
                            if (
                              currentTier === selectedTier &&
                              currentPlanMode === basePlan.planMode
                            ) {
                              return 'Current plan';
                            }
                          }
                          return 'Selected plan';
                        })()}
                        )
                      </span>
                      {selectedLFCreditPlan && (
                        <span className="font-weight-bold">
                          {' '}
                          + {selectedLFCreditPlan.name} credits{periodSuffix}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
              <span className="credit-slider-selected-price">
                {(() => {
                  if (!selectedLFCreditPlan) return '$0';
                  const isYearly = planCycle === BillingOption.ANNUALLY;
                  const periodSuffix = isYearly ? '/year' : '/month';
                  return `$${selectedLFCreditPlan.amount}${periodSuffix}`;
                })()}
              </span>
            </div>
            {filteredAddonPlans?.length > 0 && (
              <CreditSlider
                value={creditSliderSelectedValue}
                onChange={handleCreditSliderChange}
                step={1}
                min={0}
                max={filteredAddonPlans.length}
                plans={filteredAddonPlans}
              />
            )}
          </div>
        </div>
      </div>
      <div className="secondary-banner-container">
        <UpgradePlanStatic link={supportUrls.leadFinderPlanViewFAQ} />
      </div>
      <BillingSummaryModal
        show={isBillingSummaryModalShow}
        onClose={hideBillingSummaryModal}
        selectedPlanDetails={selectedPlanDetails as any}
        onPurchase={onPurchase}
        isUserModifyingSubscription={isUserModifyingSubscription}
        isPaddleFreeUser={isFreePlanPaddleUser}
        onPaddlePayNow={onPaddleFreePayNow}
        paddleBillingData={paddleBillingData}
      />

      <PaddleBillingSummaryModal
        show={isPaddleBillingSummaryModalShow}
        onClose={hidePaddleBillingSummaryModal}
        planName={selectedPlanDetails?.planName || ''}
        previewData={paddlePreviewData}
        onPayNow={onPaddlePayNow}
        isPurchaseInProgress={isPurchaseInProgress}
        isPreviewLoading={isPreviewLoading}
        onUpdateCardDetails={onUpdateCardDetails}
        isUpdatingPaymentMethod={isUpdatingPaymentMethod}
        onApplyCoupon={onPaddleApplyCoupon}
        onClearCoupon={onPaddleClearCoupon}
        isCouponApplying={isCouponApplying}
        isCouponApplied={isCouponApplied}
        isCouponInvalid={isCouponInvalid}
        leadFinderBilling={getLeadFinderBillingDetails()}
      />
      {selectedBasePlanId && (
        <div className="lead-finder-sticky-panel">
          <div className="lead-finder-sticky-panel__content">
            {(() => {
              const isYearly = planCycle === BillingOption.ANNUALLY;
              const periodLabel = isYearly ? 'year' : 'month';
              const periodShort = isYearly ? 'year' : 'month';
              const billedLabel = isYearly
                ? '(billed annually)'
                : '(billed monthly)';

              const basePlan = sendGetLeadFinderPlansResponse?.basePlans?.find(
                ({ id }) => id === selectedBasePlanId,
              );
              if (!basePlan) return null;

              const rawTier = extractTier(basePlan.planCode);
              const tier = rawTier.charAt(0).toUpperCase() + rawTier.slice(1);
              const periodLabelForName = isYearly ? 'Yearly' : 'Monthly';
              const stickyPanelDisplayName = `Lead Finder ${tier} (${periodLabelForName})`;

              const basePlanAmount = parseFloat(basePlan.amount);

              return (
                <>
                  <div className="d-flex gap-8">
                    <div className="lead-finder-sticky-panel__details">
                      <span className="lead-finder-sticky-panel__plan-name">
                        {stickyPanelDisplayName}
                      </span>
                      {selectedLFCreditPlan && (
                        <span className="lead-finder-sticky-panel__addon">
                          + {selectedLFCreditPlan.name} Credits/{periodShort}{' '}
                          {billedLabel}
                        </span>
                      )}
                    </div>
                    <div className="lead-plan-divider" />
                    <div className="lead-finder-sticky-panel__pricing">
                      <span className="lead-finder-sticky-panel__total-price">
                        ${basePlanAmount.toFixed(2)}/{periodLabel}
                      </span>
                      {selectedLFCreditPlan && (
                        <span className="lead-finder-sticky-panel__addon-price">
                          ${selectedLFCreditPlan.amount}/{periodShort}
                        </span>
                      )}
                    </div>
                  </div>
                  {isUpgradeDisabled ? (
                    <OverlayTooltip
                      text="You can't downgrade the plan"
                      placement={Placement.Top}
                    >
                      <Button
                        variant={Button.Variant.Primary}
                        theme={Button.Theme.Solid}
                        className="solid-primary-new"
                        disabled
                      >
                        Upgrade
                      </Button>
                    </OverlayTooltip>
                  ) : (
                    <Button
                      variant={Button.Variant.Primary}
                      theme={Button.Theme.Solid}
                      className="solid-primary-new"
                      onClick={handleUpgradeClick}
                    >
                      Upgrade
                    </Button>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadFinderPlans;
